import React, { useContext } from "react"

import {
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
} from "@fortawesome/free-solid-svg-icons"
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid"

import classNames from "src/classNames"

import CloudroomContext from "components/ChimeCloudroom/CloudroomContext"
import BaseButton from "components/ChimeCloudroom/Shared/BaseButton"

import useVolumeIndicator from "../hooks/useVolumeIndicator"

const LocalTile = () => {
  const {
    audioVideo,
    audioSetupInProgress,
    videoSetupInProgress,
    channel,
    muteLocalAudio,
    unmuteLocalAudio,
    muteLocalVideo,
    unmuteLocalVideo,
    localControlsLocked,
    localUser,
    roster,
    ghost,
    session,
  } = useContext(CloudroomContext)

  const localAttendee = roster[session.chimeJoinInfo.attendee.attendeeId]
  const videoOn = localAttendee?.sharingVideo
  const screenSharing = localAttendee?.sharingContent
  const selfViewHidden =
    localUser.type === "Student" && session.hideStudentSelfView

  const initialAudioMuted = session.audioMutedUserIds.includes(localUser.id)

  const { muted } = useVolumeIndicator(
    session.chimeJoinInfo.attendee.attendeeId,
    initialAudioMuted,
    audioVideo
  )

  const toggleLocalAudio = () => {
    if (audioVideo.realtimeIsLocalAudioMuted()) {
      unmuteLocalAudio()
      channel.perform("unmute_self", { type: "audio", userId: localUser.id })
    } else {
      muteLocalAudio()
      channel.perform("mute_self", { type: "audio", userId: localUser.id })
    }
  }

  const toggleLocalVideo = () => {
    if (audioVideo.hasStartedLocalVideoTile()) {
      muteLocalVideo()
      channel.perform("mute_self", { type: "video", userId: localUser.id })
    } else {
      unmuteLocalVideo()
      channel.perform("unmute_self", { type: "video", userId: localUser.id })
    }
  }

  return (
    <div className={classNames(ghost && "hidden")}>
      <div className="relative max-h-max  rounded-lg bg-neutral-800">
        <audio id="room-audio" className="hidden" />
        <video
          id="video-local"
          className={classNames(
            "aspect-video max-w-full rounded-lg object-contain",
            !screenSharing && (selfViewHidden || !videoOn) && "hidden"
          )}
        />
        <div
          className={classNames(
            "flex aspect-video h-full items-center justify-center text-[length:2cqw]",
            (screenSharing || (!selfViewHidden && videoOn)) && "hidden"
          )}
        >
          {videoOn && selfViewHidden && (
            <div className="flex flex-col items-center gap-2 px-4 text-center text-white">
              <div className="flex items-center gap-2">
                <EyeIcon className="inline h-6 w-6" aria-hidden="true" />
                <span>Your video is on</span>
              </div>
              <div className="text-sm">Other participants can see you</div>
            </div>
          )}
          {!videoOn && (
            <div className="flex flex-col items-center gap-2 px-4 text-center text-white">
              <div className="flex items-center gap-2">
                <EyeSlashIcon className="inline h-6 w-6" aria-hidden="true" />
                <span>Your video is off</span>
              </div>
              <div className="text-sm">Other participants cannot see you</div>
            </div>
          )}
        </div>
        <div className="absolute bottom-1 right-1 space-x-2">
          <BaseButton
            onClick={toggleLocalAudio}
            active={muted}
            disabled={localControlsLocked || audioSetupInProgress}
            className="w-20"
            activeIcon={faMicrophoneSlash}
            inactiveIcon={faMicrophone}
            transparent={true}
          />
          {localUser.type !== "Student" && (
            <BaseButton
              onClick={toggleLocalVideo}
              className="w-20"
              active={!videoOn}
              disabled={localControlsLocked || videoSetupInProgress}
              activeIcon={faVideoSlash}
              inactiveIcon={faVideo}
              transparent={true}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default LocalTile
